/**
 * Skor SDK theme receiver.
 *
 * Applies branding passed from a host page embedding this app in an iframe.
 * Two transports are supported:
 *  1. `?theme=<base64url json>` URL parameter (set by the SDK on iframe src,
 *     applies before first paint).
 *  2. `postMessage({ type: "skor-theme", vars: { "--skor-...": "..." } })`
 *     for live updates (SDK calls this on load and via updateTheme()).
 *
 * Only variables starting with "--" are ever applied, and only onto
 * document.documentElement — nothing else is reachable through this path.
 */

const THEME_MESSAGE_TYPE = "skor-theme";

/** Apply a flat map of CSS custom properties to :root. */
export const applySkorThemeVariables = (vars = {}) => {
  if (!vars || typeof vars !== "object") return;
  const root = document.documentElement;
  Object.entries(vars).forEach(([key, value]) => {
    if (key.startsWith("--") && typeof value === "string" && value.trim()) {
      root.style.setProperty(key, value.trim());
    }
  });
};

/** Read and apply the ?theme= param if present. Safe to call multiple times. */
export const applySkorThemeFromUrl = () => {
  try {
    const raw = new URLSearchParams(window.location.search).get("theme");
    if (!raw) return;
    // base64url → base64
    let b64 = raw.replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const json = decodeURIComponent(escape(atob(b64)));
    const parsed = JSON.parse(json);
    if (parsed?.type === THEME_MESSAGE_TYPE) {
      applySkorThemeVariables(parsed.vars);
    }
  } catch (err) {
    console.warn("Skor: ignoring invalid theme payload", err);
  }
};

/** Listen for live theme updates posted by the SDK. Returns a cleanup fn. */
export const initSkorThemeListener = () => {
  const handler = (event) => {
    // Note: origin is intentionally not restricted — the payload is inert
    // (CSS custom properties only) and validated below.
    if (event.data?.type !== THEME_MESSAGE_TYPE) return;
    applySkorThemeVariables(event.data.vars);
  };
  window.addEventListener("message", handler);
  return () => window.removeEventListener("message", handler);
};
